import { action, trackingContext, formAction } from './constants';
import {
  _TrackEventMethodProps,
  CustomerTrackingData,
  EventData,
  TrackerConstructorProps,
  TrackerMethodProps,
} from './types';

export class Tracker {
  public readonly context?: trackingContext | string;
  public readonly prefix: string;
  public group?: string;
  public category?: string;
  public extraData: Record<string, any>;
  protected _getCustomerTrackingData: () => CustomerTrackingData;

  private static _initialiseDataLayer = () => {
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = [];
    }
  };

  private static get dataLayer() {
    Tracker._initialiseDataLayer();
    return window?.dataLayer ? window.dataLayer : [];
  }

  get isEmpty() {
    return !this.context && !this.category && !this.group && !Object.keys(this.extraData).length;
  }

  constructor({
    context,
    prefix = 'custom',
    group,
    category,
    extraData,
    getCustomerTrackingData = () => ({}),
  }: TrackerConstructorProps = {}) {
    Tracker._initialiseDataLayer();
    this.context = context;
    this.prefix = prefix;
    this.group = group;
    this.category = category;
    this.extraData = extraData ?? {};
    this._getCustomerTrackingData = getCustomerTrackingData;
  }

  protected _trackEvent({ action, label, source, extraData = {}, context = this.context }: _TrackEventMethodProps) {
    if (this.isEmpty) return;
    Tracker.dataLayer.push({
      event: `${this.prefix}.${context ? `${context}.` : ''}${action}`,
      group: this.group || undefined,
      eventCategory: this.category || undefined,
      eventLabel: label || undefined,
      source: source || undefined,
      ...this.extraData,
      ...extraData,
    });
  }

  public trackPageLoad(options: Omit<TrackerMethodProps, 'label | contextOverride'> & { pageType: string }) {
    const { pageType = 'General Pages', extraData } = options;
    Tracker.dataLayer.push({
      pageType,
      ...this._getCustomerTrackingData(),
      ...(extraData ?? {}),
    });
  }

  public trackView(options?: TrackerMethodProps) {
    const { label, extraData, contextOverride } = options ?? {};
    this._trackEvent({
      action: (contextOverride ?? this.context) === trackingContext.form ? formAction.stepView : action.view,
      label,
      extraData,
      context: contextOverride,
    });
  }

  public trackClick(options?: TrackerMethodProps) {
    const { label, extraData, contextOverride, source } = options ?? {};
    this._trackEvent({
      action: (contextOverride ?? this.context) === trackingContext.modal ? action.modalClick : action.click,
      label,
      source,
      extraData,
      context: contextOverride,
    });
  }

  public trackImpression(options?: TrackerMethodProps) {
    const { label, extraData, contextOverride, source } = options ?? {};
    this._trackEvent({
      action: action.impression,
      label,
      source,
      extraData,
      context: contextOverride,
    });
  }

  public trackChange(options?: TrackerMethodProps) {
    const { label, extraData, contextOverride, source } = options ?? {};
    this._trackEvent({
      action: action.change,
      label,
      source,
      extraData,
      context: contextOverride,
    });
  }

  public trackSearch(options?: TrackerMethodProps) {
    const { label, extraData, contextOverride, source } = options ?? {};
    this._trackEvent({
      action: action.search,
      label,
      source,
      extraData,
      context: contextOverride,
    });
  }

  public trackError(options?: Omit<TrackerMethodProps, 'label'> & { errorMsg?: string }) {
    const { errorMsg, extraData = {}, contextOverride, source } = options ?? {};
    this._trackEvent({
      action: action.error,
      source,
      extraData: { errorMsg: [{ message: errorMsg }], ...extraData },
      context: contextOverride,
    });
  }

  public trackFormEvent(options: TrackerMethodProps & { action: formAction }) {
    const { label, action, extraData, contextOverride } = options;
    this._trackEvent({
      action,
      label,
      extraData,
      context: contextOverride ?? trackingContext.form,
    });
  }

  public trackCustomEvent(eventData: EventData) {
    Tracker.dataLayer.push(eventData);
  }

  public static trackCustomEvent(eventData: EventData) {
    Tracker.dataLayer.push(eventData);
  }
}
